// @ts-check
import React, { useEffect, useState } from 'react';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import './ConfirmModal.css';

/**
 * @typedef {'neutral' | 'caution' | 'destructive'} ConfirmVariant
 */

/**
 * ConfirmModal — variant-aware confirmation dialog.
 *
 * Replaces the inline ConfirmationModal that lived in LicenseTable.js. Variants
 * shape the visual emphasis and add a typed-confirmation gate for irreversible
 * actions:
 *
 *   - neutral     → Enable, Restore. Plain primary button.
 *   - caution     → Disable, Soft Delete. Danger-styled confirm button.
 *   - destructive → Permanent Delete (Purge). Requires the operator to type
 *                   `confirmText` (typically the serial number) verbatim
 *                   before the Confirm button enables.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onCancel
 * @param {() => void | Promise<void>} props.onConfirm
 * @param {string} props.title
 * @param {React.ReactNode} props.message
 * @param {ConfirmVariant} [props.variant]
 * @param {string} [props.confirmLabel]
 * @param {string} [props.cancelLabel]
 * @param {string} [props.confirmText] - destructive variant: operator must type this verbatim
 * @param {boolean} [props.submitting] - disables actions and prevents dismissal
 */
export function ConfirmModal({
  isOpen,
  onCancel,
  onConfirm,
  title,
  message,
  variant = 'neutral',
  confirmLabel,
  cancelLabel = 'Cancel',
  confirmText,
  submitting = false,
}) {
  const [typed, setTyped] = useState('');

  // Reset typed text whenever the modal opens (covers the case where the same
  // ConfirmModal instance is reused for sequential confirmations).
  useEffect(() => {
    if (isOpen) setTyped('');
  }, [isOpen, confirmText]);

  const titleId = 'confirm-modal-title';
  const isDestructive = variant === 'destructive';
  const requiresGate = isDestructive && Boolean(confirmText);
  const gateSatisfied = !requiresGate || typed === confirmText;
  const confirmDisabled = submitting || !gateSatisfied;

  const buttonVariant = variant === 'destructive' || variant === 'caution' ? 'danger' : 'primary';
  const defaultLabel = isDestructive ? 'Permanently delete' : 'Confirm';

  return (
    <Modal isOpen={isOpen} onClose={onCancel} labelledBy={titleId} dismissable={!submitting}>
      <Modal.Header id={titleId}>{title}</Modal.Header>
      <Modal.Body>
        <p className="confirm-modal__message">{message}</p>
        {requiresGate && (
          <div className="confirm-modal__gate">
            <label htmlFor="confirm-modal-gate-input" className="form-label">
              Type <code className="confirm-modal__token">{confirmText}</code> to confirm
            </label>
            <input
              id="confirm-modal-gate-input"
              type="text"
              className="form-input confirm-modal__gate-input"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              disabled={submitting}
              aria-describedby="confirm-modal-gate-hint"
            />
            <p id="confirm-modal-gate-hint" className="form-hint">
              This action cannot be undone.
            </p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>
          {cancelLabel}
        </Button>
        <Button
          variant={buttonVariant}
          onClick={onConfirm}
          disabled={confirmDisabled}
          loading={submitting}
        >
          {confirmLabel || defaultLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
